/**
 * Stripe Pricing Configuration — Pricing v4 (Abo-Modell)
 *
 * Drei Ebenen:
 *   1. Bild-Abo (Pflicht): Starter / Pro / Agency, monatlich oder jährlich
 *   2. Grundriss-Paket (Opt-in-Add-on je Plan)
 *   3. Bild-Credit-Top-ups (Slider, 1,50 €/Credit) + Einzel-Grundriss (15 €)
 *
 * WICHTIG: Die Price IDs müssen mit
 * supabase/functions/_shared/subscription-pricing.ts übereinstimmen!
 * Live-IDs eingetragen am 2026-07-06 (Stripe-Export).
 */

export type PlanId = 'starter' | 'pro' | 'agency';
export type BillingCycle = 'monthly' | 'yearly';

export interface SubscriptionPlan {
  id: PlanId;
  name: string;
  tagline: string;
  /** Bild-Credits pro Monat */
  imageCredits: number;
  /** Marketing-Orientierung, keine Systemregel (~10 Credits/Immobilie) */
  immoLabel: string;
  popular?: boolean;
  features: string[];
  /** Monatspreis in Cent bei monatlicher Zahlung */
  monthlyCents: number;
  /** Monatspreis in Cent bei jährlicher Zahlung (2 Monate gratis) */
  yearlyMonthlyCents: number;
  priceIdMonthly: string;
  priceIdYearly: string;
  /** Grundriss-Paket (Add-on) */
  addon: {
    floorplansPerMonth: number;
    monthlyCents: number;
    yearlyMonthlyCents: number;
    priceIdMonthly: string;
    priceIdYearly: string;
  };
}

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Für den Einstieg – ein, zwei Objekte im Monat.',
    imageCredits: 15,
    immoLabel: '≈ 1–2 Immobilien',
    monthlyCents: 1900,
    yearlyMonthlyCents: 1600,
    priceIdMonthly: 'price_1TqIk7K3AcRYGkPssq9ssGu6',
    priceIdYearly: 'price_1TqIkbK3AcRYGkPsjwoAMj0w',
    features: [
      '15 Bild-Credits / Monat',
      'Unbegrenzte Projekte',
      'Kommerzielle Nutzung',
      'Support via Mail',
    ],
    addon: {
      floorplansPerMonth: 1,
      monthlyCents: 1000,
      yearlyMonthlyCents: 800,
      priceIdMonthly: 'price_1TqImBK3AcRYGkPsaDYkwZvc',
      priceIdYearly: 'price_1TqImNK3AcRYGkPsz5ppeYtF',
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Für aktive Makler – der Sweet Spot.',
    imageCredits: 50,
    immoLabel: '≈ 4–6 Immobilien',
    popular: true,
    monthlyCents: 5900,
    yearlyMonthlyCents: 4900,
    priceIdMonthly: 'price_1TqIl1K3AcRYGkPs8oW9wjnc',
    priceIdYearly: 'price_1TqIlFK3AcRYGkPsdvzqTUDA',
    features: [
      'Alles aus Starter, plus:',
      '50 Bild-Credits / Monat',
      'Priorisierte Generierung',
      'Referenz-/Moodboard-Staging',
      'Priorisierter Support',
    ],
    addon: {
      floorplansPerMonth: 5,
      monthlyCents: 4000,
      yearlyMonthlyCents: 3300,
      priceIdMonthly: 'price_1TqImfK3AcRYGkPspReiNhtk',
      priceIdYearly: 'price_1TqIn3K3AcRYGkPsRzHbEgCU',
    },
  },
  {
    id: 'agency',
    name: 'Agency',
    tagline: 'Für Teams & hohes Volumen. Bester Preis.',
    imageCredits: 129,
    immoLabel: '≈ 10+ Immobilien',
    monthlyCents: 12900,
    yearlyMonthlyCents: 10900,
    priceIdMonthly: 'price_1TqIlZK3AcRYGkPsozxKSIA5',
    priceIdYearly: 'price_1TqIlnK3AcRYGkPsBlt0qohC',
    features: [
      'Alles aus Pro, plus:',
      '129 Bild-Credits / Monat',
      '3 Team-Sitze + geteilter Pool',
      'Premium-Support',
      'Bester Preis: 1,00 € / Bild',
    ],
    addon: {
      floorplansPerMonth: 12,
      monthlyCents: 9000,
      yearlyMonthlyCents: 7500,
      priceIdMonthly: 'price_1TqInMK3AcRYGkPsOSsF8JrJ',
      priceIdYearly: 'price_1TqInXK3AcRYGkPsujpUE509',
    },
  },
];

/** Bild-Credit-Top-up: 1,50 € pro Credit, Slider 10–200 (Schritt 5) */
export const TOPUP_CONFIG = {
  unitCents: 150,
  min: 10,
  max: 200,
  step: 5,
  defaultValue: 30,
} as const;

/** Einzel-Grundriss (2D + 3D): 15 €, kein Abo nötig, verfällt nie */
export const FLOORPLAN_SINGLE_CENTS = 1500;

export const getPlanById = (id: string): SubscriptionPlan | undefined =>
  SUBSCRIPTION_PLANS.find((plan) => plan.id === id);

/** Monatspreis (Cent) des Plans für den gegebenen Zyklus */
export const planMonthlyCents = (plan: SubscriptionPlan, cycle: BillingCycle): number =>
  cycle === 'yearly' ? plan.yearlyMonthlyCents : plan.monthlyCents;

/** Monatspreis (Cent) des Grundriss-Add-ons für den gegebenen Zyklus */
export const addonMonthlyCents = (plan: SubscriptionPlan, cycle: BillingCycle): number =>
  cycle === 'yearly' ? plan.addon.yearlyMonthlyCents : plan.addon.monthlyCents;

/** ≈ €/Bild als formatierter String (z. B. "≈ 1,27 € / Bild") */
export const perImageLabel = (plan: SubscriptionPlan, cycle: BillingCycle): string => {
  const cents = planMonthlyCents(plan, cycle) / plan.imageCredits;
  return `≈ ${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € / Bild`;
};

/**
 * Formatiere Preis für Anzeige
 */
export const formatPrice = (cents: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
};

/** Ganzzahliger Euro-Betrag ohne Nachkommastellen (z. B. "19 €") */
export const formatEuro = (cents: number): string =>
  `${Math.round(cents / 100).toLocaleString('de-DE')} €`;
